/**
 * @expFlag public-app-download-banner
 * @ticket CLOUDWEB-20131
 * @file
 */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    abPublicAppDownloadBannerTouchSelector,
    publicAppDownloadBannerTouch,
} from 'reactApp/appHelpers/featuresHelpers/features/publicAppDownloadBannerTouch';
import { chooseVariant } from 'reactApp/appHelpers/featuresHelpers/utils';
import { getPublicItemType } from 'reactApp/modules/public/public.helpers';
import { getCurrentPublicItem } from 'reactApp/modules/public/public.selectors';
import { hideSnackbarAction, setZIndexAction, showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { sendDwh as sendPublicDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

const DOWNLOAD_APP_SNAKCBAR_ID = 'download-app';

export const usePublicSnackbar = (showSnackbar: boolean, isSelecting: boolean, zIndex?: number) => {
    const dispatch = useDispatch();
    const publicItem = useSelector(getCurrentPublicItem);
    const wasSnackbarShownRef = useRef(false);
    const userClosedSnackbarRef = useRef(false);

    const deepLink = `?mt_deeplink=${window.location.href}`;
    const link = publicAppDownloadBannerTouch ? publicAppDownloadBannerTouch.href : '';
    const snackbarText = chooseVariant(() => abPublicAppDownloadBannerTouchSelector(), {
        control: '',
        variant2: 'В приложении — файлы по папкам',
        variant3: 'Всё удобнее — в приложении',
    });

    const handleOnMobileAppClick = useCallback(() => {
        sendPublicDwh({
            eventCategory: ECategoryGa.publicActionToInstall,
            action: 'click-download',
            dwhData: {
                type_public: publicItem ? getPublicItemType(publicItem) : undefined,
                type_element: 'snackbar',
            },
        });

        setTimeout(() => {
            window.location.href = link + deepLink;
        }, 100);
    }, [publicItem, deepLink, link]);

    useEffect(() => {
        if (zIndex !== undefined) {
            dispatch(setZIndexAction(zIndex));
        }
        return () => {
            dispatch(setZIndexAction(undefined));
        };
    }, [zIndex, dispatch]);

    const handleOnClose = useCallback(() => {
        if (userClosedSnackbarRef.current) {
            return;
        }
        userClosedSnackbarRef.current = true;
        sendPublicDwh({
            eventCategory: ECategoryGa.publicActionToInstall,
            action: 'close',
            dwhData: {
                type_public: publicItem ? getPublicItemType(publicItem) : undefined,
                type_element: 'snackbar',
            },
        });
    }, [publicItem]);

    useEffect(() => {
        if (showSnackbar && publicItem) {
            if (isSelecting || userClosedSnackbarRef.current) {
                // Если идет выбор или пользователь закрыл его, скрываем
                dispatch(hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID));
                wasSnackbarShownRef.current = false;
                return;
            }

            const showSnackbarActionObj = showSnackbarAction({
                id: DOWNLOAD_APP_SNAKCBAR_ID,
                type: SnackbarTypes.mobileAppDownload,
                text: snackbarText,
                iconSize: 'flex',
                closable: true,
                disableCloseTimeout: true,
                buttonText: 'Скачать Облако',
                onButtonClick: handleOnMobileAppClick,
                onClose: handleOnClose,
            });

            if (!wasSnackbarShownRef.current) {
                sendPublicDwh({
                    eventCategory: ECategoryGa.publicActionToInstall,
                    action: 'show',
                    dwhData: {
                        type_public: publicItem ? getPublicItemType(publicItem) : undefined,
                        type_element: 'snackbar',
                    },
                });
                wasSnackbarShownRef.current = true;
            }

            dispatch(showSnackbarActionObj);

            return () => {
                dispatch(hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID));
            };
        }
    }, [showSnackbar, isSelecting, dispatch, handleOnMobileAppClick, publicItem, snackbarText, handleOnClose]);
};
